import {
  Button,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { OneSupplierNote } from "./OneSupplierNote";
import {
  MAX_RELATIVE_ADVANTAGES_LENGTH,
  relativeAdvantagesHelpText,
  relativeAdvantagesTooLong,
} from "./relativeAdvantages";
import { useTranslation } from "react-i18next";
import { EndedContractWinnersAlert } from "./EndedContractWinnersAlert";
import { UnassessedWinnersAlert } from "./UnassessedWinnersAlert";
import {
  BidStatus,
  FrameworkAgreementType,
  Currency,
  NegotiationRoundStatus,
  SourcingEventDto,
  type EvalBidDto,
  Regulation,
} from "../../../autogen/bff-api";
import { CompleteRoundButton } from "../negotiation/CompleteRoundButton";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { ListLoadError } from "../../../common/ListLoadError";
import { useAwardSelection, type CeilingProblem } from "./AwardSelectionContext";
import { displaySum } from "../../../common/formatting/displaySum";
import {
  failsMinimumRequirement,
  hasUnassessedMinimumRequirements,
} from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { StandstillChoiceField } from "./StandstillChoiceField";
import { VoluntaryStandstillField } from "./VoluntaryStandstillField";
import { SupplierCountIndicator } from "./SupplierCountIndicator";
import { REVEAL_ANCHORS } from "../revealAnchor";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { relativeAdvantagesLabel } from "./relativeAdvantagesLabel";

export const SelectWinner = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { rounds } = useRoundSelection();
  // ADR-033 §5: awarding is blocked while any negotiation round is still open.
  const hasOpenRound = rounds.some((round) => round.status === NegotiationRoundStatus.Published);
  const completableRound = [...rounds]
    .filter((round) => round.status === NegotiationRoundStatus.Published && Date.parse(round.deadline) <= Date.now())
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1);
  const {
    evaluation,
    isLoading,
    reloadEvaluation,
    isReloadingEvaluation,
    winnerIds,
    toggleWinner,
    awardsOneSupplier,
    keptOneOfSeveral,
    internalNote,
    setInternalNote,
    relativeAdvantages,
    setRelativeAdvantages,
    currency,
    currencyChoices,
    setCurrency,
    amount,
    setAmount,
    winnersTotal,
    winnersShareAFramework,
    buyerSetsStandstill,
    choosesVoluntaryStandstill,
    juryRankingDeparture,
    ceilingProblem,
    endedContractWinners = [],
    unassessedWinners = [],
  } = useAwardSelection();
  const selectWinnerPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const maxParticipants = selectWinnerPublicCtx?.frameworkMaxParticipants ?? undefined;
  const designContest = eventIsDesignContest(event);
  const winnersPrice =
    winnersTotal?.value != null ? displaySum({ value: winnersTotal.value, currency: winnersTotal.currency }) : undefined;
  const relativeAdvantagesHelp = relativeAdvantagesHelpText(t, event, designContest);

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (!evaluation) {
    return (
      <ListLoadError
        message={t("Could not load the evaluation.")}
        onRetry={reloadEvaluation}
        isRetrying={isReloadingEvaluation}
      />
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  return (
    <Stack
      id={REVEAL_ANCHORS.winnerSelection}
      sx={{ scrollMarginTop: "1rem" }}
      spacing="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
    >
      <AwardDecisionHistory event={event} />


      {hasOpenRound && (
        <Alert status="info" borderRadius="md" alignItems="center">
          <AlertIcon />
          <Text flex="1">{t("Complete all negotiation rounds before awarding")}</Text>
          {completableRound && <CompleteRoundButton round={completableRound} />}
        </Alert>
      )}

      <Stack spacing="2">
        {awardsOneSupplier && <OneSupplierNote keptOneOfSeveral={keptOneOfSeveral} />}
        <AwardBidsTable
          evaluation={evaluation}
          mode="select"
          selectedBidIds={winnerIds}
          onToggle={toggleWinner}
          awardsOneSupplier={awardsOneSupplier}
        />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      {juryRankingDeparture === "REFUSED_BY_BINDING_JURY" && (
        <Alert status="warning" rounded="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {t("The jury's decision is binding, so the winners must be the entries the jury ranked highest.")}
          </AlertDescription>
        </Alert>
      )}

      <MinimumRequirementWarning
        notFulfilled={supplierNames(evaluation.bids, winnerIds, failsMinimumRequirement)}
        unassessed={supplierNames(evaluation.bids, winnerIds, hasUnassessedMinimumRequirements)}
      />

      {ceilingProblem && <CeilingAlert problem={ceilingProblem} currency={currency} />}
      <EndedContractWinnersAlert suppliers={endedContractWinners} />
      <UnassessedWinnersAlert eventId={event.id} winners={unassessedWinners} />

      {!designContest && (
      <FormControl isInvalid={ceilingProblem?.kind === "OVER_CEILING" || ceilingProblem?.kind === "VALUE_REQUIRED"}>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder={winnersPrice}
          />
          {ceilingProblem?.kind === "FRAMEWORK_LOADING" ? (
            <Spinner size="sm" aria-label={t("Loading the framework agreement") ?? undefined} />
          ) : (
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            isDisabled={currencyChoices.length === 1}
            aria-label={t("Currency") ?? "Currency"}
            bg="smBackground"
            maxW="32"
          >
            {currencyChoices.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          )}
        </HStack>
        {amount.trim() === "" && winnersPrice && (
          <FormHelperText data-testid="contract-value-default">
            {t("Left empty, the contract's value is the winners' total: {{price}}", {
              count: winnerIds.length,
              price: winnersPrice,
            })}
          </FormHelperText>
        )}
        {amount.trim() === "" && winnerIds.length > 0 && !winnersPrice && !ceilingProblem && (
          <FormHelperText data-testid="contract-value-no-default">
            {winnersShareAFramework
              ? t("Tenders on a shared framework agreement can't be added together. Enter the contract's value here, or it stays empty in the protocol.")
              : t("No total price from the winners to use. Enter the contract's value here, or it stays empty in the protocol.", {
                  count: winnerIds.length,
                })}
          </FormHelperText>
        )}
      </FormControl>
      )}

      {choosesVoluntaryStandstill && (
        <VoluntaryStandstillField
          utilitiesPartIII={event.standstillUnderUtilitiesPartIII ?? false}
          partI={event.standstillWithoutProvision ?? false}
        />
      )}
      {buyerSetsStandstill && (
        <StandstillChoiceField
          withoutProvision={event.standstillWithoutProvision ?? false}
          underPartIV={event.standstillUnderPartIV ?? false}
          underUtilities={selectWinnerPublicCtx?.regulation === Regulation.Forsyningsforskriften}
        />
      )}

      <FormControl
        id={REVEAL_ANCHORS.relativeAdvantages}
        sx={{ scrollMarginTop: "1rem" }}
        isInvalid={relativeAdvantagesTooLong(relativeAdvantages)}
      >
        <FormLabel>{relativeAdvantagesLabel(t, designContest)}</FormLabel>
        <Textarea
          value={relativeAdvantages}
          onChange={(e) => setRelativeAdvantages(e.target.value)}
          bg="smBackground"
          rows={4}
        />
        <FormHelperText>{relativeAdvantagesHelp}</FormHelperText>
        <FormHelperText mt="1">
          {t("{{count}} / {{max}} characters", {
            count: relativeAdvantages.trim().length,
            max: MAX_RELATIVE_ADVANTAGES_LENGTH,
          })}
        </FormHelperText>
        <FormErrorMessage>
          {t("The text can be at most {{max}} characters.", { max: MAX_RELATIVE_ADVANTAGES_LENGTH })}
        </FormErrorMessage>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Text fontSize="sm" color="smMuted">
        {designContest
          ? t("You'll prepare and send the letters to the participants after choosing the winners.")
          : t("You'll prepare and send award letters to each supplier after awarding.")}
      </Text>

    </Stack>
  );
};

const CeilingAlert = ({ problem, currency }: { problem: CeilingProblem; currency: Currency }) => {
  const { t } = useTranslation();
  if (problem.kind === "FRAMEWORK_LOADING") return null;
  if (problem.kind === "FRAMEWORK_UNAVAILABLE") {
    return (
      <Alert status="error" rounded="md" fontSize="sm" alignItems="flex-start">
        <AlertIcon />
        <Stack spacing="2" align="flex-start">
          <AlertDescription>
            {t("The framework agreement could not be loaded, so the award can't be checked against its ceiling value.")}
          </AlertDescription>
          <Button size="sm" variant="outline" onClick={problem.retry}>
            {t("Try again")}
          </Button>
        </Stack>
      </Alert>
    );
  }
  if (problem.kind === "TENDER_IN_ANOTHER_CURRENCY") {
    return (
      <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
        <AlertIcon />
        <Stack spacing="1">
          <AlertTitle>
            {t("The selected tender is priced in {{tenderCurrency}}, but the framework agreement is in {{frameworkCurrency}}", {
              tenderCurrency: problem.tenderPrice.currency,
              frameworkCurrency: problem.frameworkCurrency,
            })}
          </AlertTitle>
          <AlertDescription>
            {t(
              "The call-off is worth the tender's price, and it has to be counted in the framework agreement's currency. Correct the tender's price in {{frameworkCurrency}} under «Evaluation» before you award.",
              { frameworkCurrency: problem.frameworkCurrency },
            )}
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }
  if (problem.remaining === null) return <UndisclosedCeilingAlert kind={problem.kind} />;
  const remaining = displaySum({ value: Math.max(problem.remaining, 0), currency });
  if (problem.kind === "TENDER_OVER_CEILING") {
    return (
      <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
        <AlertIcon />
        <Stack spacing="1">
          <AlertTitle>{t("The selected tender doesn't fit under the framework agreement's ceiling value")}</AlertTitle>
          <AlertDescription>
            {t(
              "The tender's price is {{price}}, and {{remaining}} is left under the ceiling value. The call-off is worth the tender's price, so this mini-competition can't be awarded. Cancel it and run a new competition for the purchase.",
              { price: displaySum(problem.tenderPrice), remaining },
            )}
          </AlertDescription>
        </Stack>
      </Alert>
    );
  }
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="1">
        <AlertTitle>
          {problem.kind === "OVER_CEILING"
            ? t("This award would take the framework agreement past its ceiling value")
            : t("Enter the contract value to award under the framework agreement")}
        </AlertTitle>
        <AlertDescription>
          {problem.kind === "OVER_CEILING"
            ? t(
                "{{remaining}} is left under the ceiling value. Lower the contract value so it fits, or cancel this mini-competition and run a new competition for the purchase.",
                { remaining },
              )
            : t("{{remaining}} is left under the ceiling value, and the award has to fit under it.", { remaining })}
        </AlertDescription>
      </Stack>
    </Alert>
  );
};

const UndisclosedCeilingAlert = ({ kind }: { kind: "OVER_CEILING" | "VALUE_REQUIRED" | "TENDER_OVER_CEILING" }) => {
  const { t } = useTranslation();
  const title =
    kind === "VALUE_REQUIRED"
      ? t("Enter the contract value to award under the framework agreement")
      : kind === "OVER_CEILING"
        ? t("This award would take the framework agreement past its ceiling value")
        : t("The selected tender doesn't fit under the framework agreement's ceiling value");
  const description =
    kind === "TENDER_OVER_CEILING"
      ? t(
          "The call-off is worth the tender's price, so this mini-competition can't be awarded. Cancel it and run a new competition for the purchase.",
        )
      : kind === "OVER_CEILING"
        ? t("Nothing is left under the ceiling value, so this mini-competition can't be awarded. Cancel it and run a new competition for the purchase.")
        : t("The award has to fit under the framework agreement's ceiling value. It is checked when you award.");
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="1">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>{description}</AlertDescription>
      </Stack>
    </Alert>
  );
};

const supplierNames = (
  bids: EvalBidDto[],
  winnerIds: string[],
  matches: (bid: EvalBidDto) => boolean,
): string[] =>
  bids.filter((bid) => winnerIds.includes(bid.bidId) && matches(bid)).map((bid) => bid.supplierName);

const MinimumRequirementWarning = ({
  notFulfilled,
  unassessed,
}: {
  notFulfilled: string[];
  unassessed: string[];
}) => {
  const { t } = useTranslation();
  if (notFulfilled.length === 0 && unassessed.length === 0) return null;
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="2">
        {notFulfilled.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fulfilled: {{suppliers}}", {
                suppliers: notFulfilled.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "A tender that does not meet an absolute minimum requirement must be rejected, not awarded. Review the minimum requirements before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
        {unassessed.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fully assessed: {{suppliers}}", {
                suppliers: unassessed.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "You do not yet know whether the tender meets the minimum requirements. Assess them before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
      </Stack>
    </Alert>
  );
};
